"use client";

import React, { useState, useEffect } from "react";
import CommonSchema from "@/components/common/CommonSchema";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useInquiryForm } from "@/hooks/useInquiryForm";
import { motion } from "motion/react";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Send,
  User,
  Calendar,
  MessageSquare,
  CheckCircle,
} from "lucide-react";
import { FadeInSection } from "@/components/common/FadeInSection";
import { ICMS, ISettings } from "@/slices/homeSlice";
import { cleanHeaderTags, createLookupObject } from "@/_helper/helper";
import { Banner } from "../common/Banner";
import Link from "next/link";
import * as Yup from "yup";

const tomorrow = new Date();
tomorrow.setDate(tomorrow.getDate() + 1);
const tomorrowStr = tomorrow.toISOString().split("T")[0];

const dayAfterTomorrow = new Date();
dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
const dayAfterTomorrowStr = dayAfterTomorrow.toISOString().split("T")[0];

const contactInquiryValidationSchema = Yup.object({
  FirstName: Yup.string().required("Name is required"),
  MobileNo: Yup.string()
    .matches(/^[0-9+\s-]{10,15}$/, "Invalid phone number")
    .required("Phone number is required"),
  Email: Yup.string()
    .email("Invalid email address")
    .required("Email is required"),
  CheckIn: Yup.date()
    .required("Required")
    .min(tomorrowStr, "Check-in must be at least tomorrow"),
  CheckOut: Yup.date().nullable(),
  Remarks: Yup.string().required("Message is required"),
});

export default function ContactPage({
  cmsData,
  initialBanner,
}: {
  cmsData: ICMS[];
  initialBanner?: any;
}) {
  const SettingList = useAppSelector((state) => state.home.SettingList);
  const BannerList = useAppSelector((state) => state.home.BannerList);

  const banner: any =
    initialBanner ||
    BannerList?.find((banner: any) => banner?.BannerType === "contact") ||
    {};
  const [lookUpCMSData, setLookUpCMSData] = useState<Record<string, ICMS>>({});
  useEffect(() => {
    if (cmsData && cmsData.length > 0) {
      setLookUpCMSData(createLookupObject(cmsData, "Name"));
    }
  }, [cmsData]);

  const { formik, submitted } = useInquiryForm(
    "General Inquiry",
    contactInquiryValidationSchema,
  );

  const Settings: Record<string, ISettings> = SettingList?.reduce(
    (acc, item) => {
      acc[item?.SettingKey] = item;
      return acc;
    },
    {} as Record<string, ISettings>,
  );

  const WhatsAppNo = Settings?.["WhatsAppNo"]?.SettingValue;
  const PhoneNo = Settings?.["PhoneNo"]?.SettingValue;
  const PhoneNo2 = Settings?.["PhoneNo2"]?.SettingValue;
  const PhoneNo3 = Settings?.["PhoneNo3"]?.SettingValue;

  const EmailID = Settings?.["EmailID"]?.SettingValue;
  const EmailID2 = Settings?.["EmailID2"]?.SettingValue;
  const EmailID3 = Settings?.["EmailID3"]?.SettingValue;

  const Address = Settings?.["Address"]?.SettingValue;
  const Address2 = Settings?.["Address2"]?.SettingValue;
  const Address3 = Settings?.["Address3"]?.SettingValue;

  const DistanceFrom = Settings?.["DistanceFrom"]?.SettingValue;

  const AddressMap = Settings?.["AddressMap"]?.SettingValue;
  const AddressMap2 = Settings?.["AddressMap2"]?.SettingValue;
  const AddressMap3 = Settings?.["AddressMap3"]?.SettingValue;

  const AddressIframe = Settings?.["AddressIframe"]?.SettingValue;

  const BusinessHours = Settings?.["BusinessHours"]?.SettingValue;
  const BusinessHours2 = Settings?.["BusinessHours2"]?.SettingValue;

  const FacebookLink = Settings?.["FacebookLink"]?.SettingValue;
  const InstaLink = Settings?.["InstaLink"]?.SettingValue;
  const XLink = Settings?.["XLink"]?.SettingValue;
  const LinkedInLink = Settings?.["LinkedInLink"]?.SettingValue;
  const YoutubeLink = Settings?.["YoutubeLink"]?.SettingValue;

  const CompanyName = Settings?.["CompanyName"]?.SettingValue;

  const mapContent = React.useMemo(
    () => ({ __html: AddressIframe }),
    [AddressIframe],
  );

  return (
    <div className="min-h-screen bg-[#fdfcfa]">
      <CommonSchema />
      <script
        id="LocalBusiness-schema"
        type="application/ld+json"
        suppressHydrationWarning
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            name: "Sarasiruham Resort",
            image: banner?.BannerImage || "",
            description:
              "Luxury 4.5-star resort in Udaipur with private pool villas, spa, and lakeside location near Eklingji Temple",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Lake Bagela, Nagda",
              addressLocality: "Eklingji",
              addressRegion: "Udaipur",
              postalCode: "313202",
              addressCountry: "IN",
            },
            telephone: "+919105700099",
            email: "booking@sarasiruhamresort.com",
            url: "https://sarasiruhamresort.com",
            sameAs: [
              "https://www.instagram.com/sarasiruham/",
              "https://www.facebook.com/sarasiruham/",
              "https://www.linkedin.com/company/sarasiruham-resort",
              "https://www.tripadvisor.com/Hotel_Review-g1187340-d3073202560-Reviews-Sarasiruham_Resort_Private_Pool_Villa-Eklingji_Udaipur_District_Rajasthan.html",
            ],
          }),
        }}
      />
      {/* Hero Section */}
      {/* <section className="pt-40 pb-16 bg-gradient-to-br from-[#5a6f3e] via-[#4a5f2e] to-[#5a6f3e] text-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1
              className="text-5xl md:text-6xl lg:text-7xl mb-6 leading-tight"
              style={{
                fontFamily: "Cormorant Garamond, serif",
                fontWeight: 400,
              }}
            >
              Get in Touch with{" "}
              <span style={{ fontWeight: 600, fontStyle: "italic" }}>
                Sarasiruham Resort
              </span>
            </h1>
            <p
              className="text-xl text-white/90 max-w-3xl mx-auto"
              style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
            >
              Planning your stay or have a query? Our team is available to
              assist you with bookings, special requests, and personalized
              experiences.
            </p>
          </motion.div>
        </div>
      </section> */}
      <Banner
        title={banner?.BannerTitle || "Contact Us"}
        description={banner?.Description}
        bgImage={banner?.BannerImage}
        // icon={<Phone className="w-16 h-16 text-[#d4af37]" />}
      />

      {/* Contact Information & Form */}
      <section className="py-20 bg-[#fdfcfa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Information - 2 columns */}
            <div className="lg:col-span-2 space-y-8">
              <FadeInSection>
                <h2
                  className="text-3xl md:text-4xl text-[#2a2520] mb-8"
                  style={{
                    fontFamily: "Cormorant Garamond, serif",
                    fontWeight: 600,
                  }}
                >
                  Contact Information
                </h2>
              </FadeInSection>

              {/* Address */}
              <FadeInSection delay={0.1}>
                <motion.div
                  whileHover={{ x: 5 }}
                  className="bg-white p-6 shadow-lg border-l-4 border-[#d4af37]"
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-[#5a6f3e]/10 p-3 rounded-full">
                      <MapPin className="w-6 h-6 text-[#5a6f3e]" />
                    </div>
                    <div>
                      <h3
                        className="text-lg font-semibold text-[#2a2520] mb-2"
                        style={{ fontFamily: "Jost, sans-serif" }}
                      >
                        Resort Address
                      </h3>
                      <p
                        className="text-[#2a2520]/70 leading-relaxed"
                        style={{
                          fontFamily: "Jost, sans-serif",
                          fontWeight: 300,
                        }}
                      >
                        {Address && (
                          <Link href={AddressMap || "#"}>
                            {Address}
                            <br />
                          </Link>
                        )}
                        {Address2 && (
                          <Link href={AddressMap2 || "#"}>
                            {Address2}
                            <br />
                          </Link>
                        )}
                        {Address3 && (
                          <Link href={AddressMap3 || "#"}>
                            {Address3}
                            <br />
                          </Link>
                        )}
                        {DistanceFrom && (
                          <>
                            <span className="text-sm text-[#5a6f3e] mt-2 inline-block">
                              {DistanceFrom}
                            </span>
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </FadeInSection>

              {/* Phone */}
              <FadeInSection delay={0.2}>
                <motion.div
                  whileHover={{ x: 5 }}
                  className="bg-white p-6 shadow-lg border-l-4 border-[#d4af37]"
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-[#5a6f3e]/10 p-3 rounded-full">
                      <Phone className="w-6 h-6 text-[#5a6f3e]" />
                    </div>
                    <div>
                      <h3
                        className="text-lg font-semibold text-[#2a2520] mb-2"
                        style={{ fontFamily: "Jost, sans-serif" }}
                      >
                        Call Us
                      </h3>
                      <div
                        className="space-y-1 text-[#2a2520]/70"
                        style={{
                          fontFamily: "Jost, sans-serif",
                          fontWeight: 300,
                        }}
                      >
                        {PhoneNo && (
                          <p>
                            <Link
                              href={`tel:${PhoneNo}`}
                              className="hover:text-[#5a6f3e] transition-colors"
                            >
                              {PhoneNo || "+91 91057 00099"}
                            </Link>
                          </p>
                        )}
                        {PhoneNo2 && (
                          <p>
                            <Link
                              href={`tel:${PhoneNo2}`}
                              className="hover:text-[#5a6f3e] transition-colors"
                            >
                              {PhoneNo2}
                            </Link>
                          </p>
                        )}
                        {PhoneNo3 && (
                          <p>
                            <Link
                              href={`tel:${PhoneNo3}`}
                              className="hover:text-[#5a6f3e] transition-colors"
                            >
                              {PhoneNo3}
                            </Link>
                          </p>
                        )}
                        <p className="text-xs text-[#5a6f3e] mt-2">
                          Available 24/7
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </FadeInSection>

              {/* Email */}
              <FadeInSection delay={0.3}>
                <motion.div
                  whileHover={{ x: 5 }}
                  className="bg-white p-6 shadow-lg border-l-4 border-[#d4af37]"
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-[#5a6f3e]/10 p-3 rounded-full">
                      <Mail className="w-6 h-6 text-[#5a6f3e]" />
                    </div>
                    <div>
                      <h3
                        className="text-lg font-semibold text-[#2a2520] mb-2"
                        style={{ fontFamily: "Jost, sans-serif" }}
                      >
                        Email Us
                      </h3>
                      <div
                        className="space-y-1 text-[#2a2520]/70"
                        style={{
                          fontFamily: "Jost, sans-serif",
                          fontWeight: 300,
                        }}
                      >
                        {EmailID && (
                          <p>
                            <Link
                              href={`mailto:${EmailID}`}
                              target="_blank"
                              className="hover:text-[#5a6f3e] transition-colors break-all"
                            >
                              {EmailID}
                            </Link>
                          </p>
                        )}
                        {EmailID2 && (
                          <p>
                            <Link
                              href={`mailto:${EmailID2}`}
                              target="_blank"
                              className="hover:text-[#5a6f3e] transition-colors break-all"
                            >
                              {EmailID2}
                            </Link>
                          </p>
                        )}
                        {EmailID3 && (
                          <p>
                            <Link
                              href={`mailto:${EmailID3}`}
                              target="_blank"
                              className="hover:text-[#5a6f3e] transition-colors break-all"
                            >
                              {EmailID3}
                            </Link>
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </motion.div>
              </FadeInSection>

              {/* Quick Contact */}
              {WhatsAppNo && (
                <FadeInSection delay={0.4}>
                  <div className="bg-gradient-to-br from-[#5a6f3e] to-[#4a5f2e] p-6 text-white shadow-xl">
                    <h3
                      className="text-xl font-semibold mb-4"
                      style={{ fontFamily: "Cormorant Garamond, serif" }}
                    >
                      Quick Contact Options
                    </h3>
                    <motion.a
                      href={`https://wa.me/${(WhatsAppNo || PhoneNo || "").replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      whileHover={{ scale: 1.05 }}
                      className="flex items-center gap-3 bg-[#25D366] px-6 py-3 mb-3 shadow-lg hover:shadow-xl transition-all"
                      style={{
                        fontFamily: "Jost, sans-serif",
                        fontWeight: 600,
                      }}
                    >
                      <MessageSquare className="w-5 h-5" />
                      WhatsApp Booking
                    </motion.a>
                    <div
                      className="flex items-center gap-2 text-sm text-white/80"
                      style={{ fontFamily: "Jost, sans-serif" }}
                    >
                      <Clock className="w-4 h-4" />
                      Instant response during business hours
                    </div>
                  </div>
                </FadeInSection>
              )}
            </div>

            {/* Enquiry Form - 3 columns */}
            <div className="lg:col-span-3">
              <FadeInSection delay={0.5}>
                <div className="bg-white shadow-2xl p-8 md:p-12 border border-[#5a6f3e]/10">
                  <h2
                    className="text-3xl md:text-4xl text-[#2a2520] mb-3"
                    style={{
                      fontFamily: "Cormorant Garamond, serif",
                      fontWeight: 600,
                    }}
                  >
                    Send Us a Message
                  </h2>
                  <p
                    className="text-[#2a2520]/70 mb-8"
                    style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
                  >
                    Fill out the form below and our team will get back to you
                    within 24 hours
                  </p>

                  {submitted ? (
                    <motion.div
                      initial={{ scale: 0.8, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      className="text-center py-12"
                    >
                      <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
                      <h3
                        className="text-2xl text-[#2a2520] mb-2"
                        style={{
                          fontFamily: "Cormorant Garamond, serif",
                          fontWeight: 600,
                        }}
                      >
                        Thank You!
                      </h3>
                      <p
                        className="text-[#2a2520]/70"
                        style={{ fontFamily: "Jost, sans-serif" }}
                      >
                        Your message has been sent successfully. We&apos;ll
                        contact you soon!
                      </p>
                    </motion.div>
                  ) : (
                    <form onSubmit={formik.handleSubmit} className="space-y-6">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label
                            htmlFor="FirstName"
                            className="block text-[#2a2520] mb-2 font-medium"
                            style={{ fontFamily: "Jost, sans-serif" }}
                          >
                            Full Name *
                          </label>
                          <div
                            className={`flex items-center gap-2 border-2 px-4 py-3 bg-[#f5f1ec]/30 transition-colors ${formik.touched.FirstName && formik.errors.FirstName ? "border-red-500" : "border-[#5a6f3e]/30 focus-within:border-[#5a6f3e]"}`}
                          >
                            <User className="w-5 h-5 text-[#5a6f3e]" />
                            <input
                              type="text"
                              id="FirstName"
                              {...formik.getFieldProps("FirstName")}
                              className="bg-transparent outline-none w-full"
                              style={{ fontFamily: "Jost, sans-serif" }}
                              placeholder="Enter your name"
                            />
                          </div>
                          {formik.touched.FirstName &&
                            formik.errors.FirstName && (
                              <p className="text-red-500 text-xs mt-1 font-medium">
                                {formik.errors.FirstName}
                              </p>
                            )}
                        </div>

                        <div>
                          <label
                            htmlFor="MobileNo"
                            className="block text-[#2a2520] mb-2 font-medium"
                            style={{ fontFamily: "Jost, sans-serif" }}
                          >
                            Phone Number *
                          </label>
                          <div
                            className={`flex items-center gap-2 border-2 px-4 py-3 bg-[#f5f1ec]/30 transition-colors ${formik.touched.MobileNo && formik.errors.MobileNo ? "border-red-500" : "border-[#5a6f3e]/30 focus-within:border-[#5a6f3e]"}`}
                          >
                            <Phone className="w-5 h-5 text-[#5a6f3e]" />
                            <input
                              type="tel"
                              id="MobileNo"
                              {...formik.getFieldProps("MobileNo")}
                              className="bg-transparent outline-none w-full"
                              style={{ fontFamily: "Jost, sans-serif" }}
                              placeholder="+91 XXXXX XXXXX"
                            />
                          </div>
                          {formik.touched.MobileNo &&
                            formik.errors.MobileNo && (
                              <p className="text-red-500 text-xs mt-1 font-medium">
                                {formik.errors.MobileNo}
                              </p>
                            )}
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="Email"
                          className="block text-[#2a2520] mb-2 font-medium"
                          style={{ fontFamily: "Jost, sans-serif" }}
                        >
                          Email Address *
                        </label>
                        <div
                          className={`flex items-center gap-2 border-2 px-4 py-3 bg-[#f5f1ec]/30 transition-colors ${formik.touched.Email && formik.errors.Email ? "border-red-500" : "border-[#5a6f3e]/30 focus-within:border-[#5a6f3e]"}`}
                        >
                          <Mail className="w-5 h-5 text-[#5a6f3e]" />
                          <input
                            type="email"
                            id="Email"
                            {...formik.getFieldProps("Email")}
                            className="bg-transparent outline-none w-full"
                            style={{ fontFamily: "Jost, sans-serif" }}
                            placeholder="your@email.com"
                          />
                        </div>
                        {formik.touched.Email && formik.errors.Email && (
                          <p className="text-red-500 text-xs mt-1 font-medium">
                            {formik.errors.Email}
                          </p>
                        )}
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div>
                          <label
                            htmlFor="CheckIn"
                            className="block text-[#2a2520] mb-2 font-medium"
                            style={{ fontFamily: "Jost, sans-serif" }}
                          >
                            Check-In Date
                          </label>
                          <div
                            className={`flex items-center gap-2 border-2 px-4 py-3 bg-[#f5f1ec]/30 transition-colors ${formik.touched.CheckIn && formik.errors.CheckIn ? "border-red-500" : "border-[#5a6f3e]/30 focus-within:border-[#5a6f3e]"}`}
                          >
                            <Calendar className="w-5 h-5 text-[#5a6f3e]" />
                            <input
                              type="date"
                              id="CheckIn"
                              {...formik.getFieldProps("CheckIn")}
                              min={new Date().toISOString().split("T")[0]}
                              className="bg-transparent outline-none w-full"
                              style={{ fontFamily: "Jost, sans-serif" }}
                            />
                          </div>
                          {formik.touched.CheckIn && formik.errors.CheckIn && (
                            <p className="text-red-500 text-xs mt-1 font-medium">
                              {formik.errors.CheckIn}
                            </p>
                          )}
                        </div>

                        <div>
                          <label
                            htmlFor="CheckOut"
                            className="block text-[#2a2520] mb-2 font-medium"
                            style={{ fontFamily: "Jost, sans-serif" }}
                          >
                            Check-Out Date
                          </label>
                          <div className="flex items-center gap-2 border-2 border-[#5a6f3e]/30 px-4 py-3 bg-[#f5f1ec]/30 focus-within:border-[#5a6f3e] transition-colors">
                            <Calendar className="w-5 h-5 text-[#5a6f3e]" />
                            <input
                              type="date"
                              id="CheckOut"
                              {...formik.getFieldProps("CheckOut")}
                              min={new Date().toISOString().split("T")[0]}
                              className="bg-transparent outline-none w-full"
                              style={{ fontFamily: "Jost, sans-serif" }}
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <label
                          htmlFor="Remarks"
                          className="block text-[#2a2520] mb-2 font-medium"
                          style={{ fontFamily: "Jost, sans-serif" }}
                        >
                          Your Message
                        </label>
                        <div
                          className={`flex items-start gap-2 border-2 px-4 py-3 bg-[#f5f1ec]/30 transition-colors ${formik.touched.Remarks && formik.errors.Remarks ? "border-red-500" : "border-[#5a6f3e]/30 focus-within:border-[#5a6f3e]"}`}
                        >
                          <MessageSquare className="w-5 h-5 text-[#5a6f3e] mt-1" />
                          <textarea
                            id="Remarks"
                            {...formik.getFieldProps("Remarks")}
                            rows={5}
                            className="bg-transparent outline-none w-full resize-none"
                            style={{ fontFamily: "Jost, sans-serif" }}
                            placeholder="Tell us about your requirements, special requests, or questions..."
                          />
                        </div>
                        {formik.touched.Remarks && formik.errors.Remarks && (
                          <p className="text-red-500 text-xs mt-1 font-medium">
                            {formik.errors.Remarks}
                          </p>
                        )}
                      </div>

                      <motion.button
                        type="submit"
                        disabled={formik.isSubmitting}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`w-full bg-[#5a6f3e] text-white py-4 px-8 transition-colors shadow-lg flex items-center justify-center gap-3 ${formik.isSubmitting ? "opacity-70 cursor-not-allowed" : "hover:bg-[#4a5f2e]"}`}
                        style={{
                          fontFamily: "Jost, sans-serif",
                          fontWeight: 600,
                          fontSize: "1.125rem",
                        }}
                      >
                        {formik.isSubmitting ? (
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Send className="w-5 h-5" />
                        )}
                        {formik.isSubmitting ? "Sending..." : "Send Message"}
                      </motion.button>

                      <p
                        className="text-center text-sm text-[#2a2520]/60"
                        style={{ fontFamily: "Jost, sans-serif" }}
                      >
                        We typically respond within 24 hours
                      </p>
                    </form>
                  )}
                </div>
              </FadeInSection>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-gradient-to-br from-[#f5f1ec] to-[#fdfcfa]">
        <div className="max-w-7xl mx-auto px-6">
          <FadeInSection>
            <div className="text-center mb-12">
              <h2
                className="text-4xl md:text-5xl text-[#2a2520] mb-4"
                style={{
                  fontFamily: "Cormorant Garamond, serif",
                  fontWeight: 600,
                }}
              >
                Find Us on the Map
              </h2>
              <p
                className="text-lg text-[#2a2520]/70"
                style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
              >
                Located in the serene beauty of Lake Bagela, near Eklingji
                Temple
              </p>
            </div>
          </FadeInSection>

          <FadeInSection delay={0.2}>
            <div className="bg-white shadow-2xl p-4 border border-[#5a6f3e]/10">
              <div
                className="w-full h-96 bg-[#f5f1ec] flex items-center justify-center text-[#2a2520]/50"
                style={{ fontFamily: "Jost, sans-serif" }}
                dangerouslySetInnerHTML={mapContent}
              >
                {/* <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3630.123!2d73.7!3d24.6!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjTCsDM2JzAwLjAiTiA3M8KwNDInMDAuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Sarasiruham Resort Location"
                  className="w-full h-full"
                ></iframe> */}
              </div>
            </div>
          </FadeInSection>
        </div>
      </section>

      {/* CTA Section */}
      {lookUpCMSData["Contact - Section : CTA"] && (
        <section className="py-20 bg-gradient-to-br from-[#2a2520] via-[#3a3530] to-[#2a2520] text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <FadeInSection>
              <div
                dangerouslySetInnerHTML={{
                  __html: cleanHeaderTags(
                    lookUpCMSData["Contact - Section : CTA"]?.Template,
                  ),
                }}
              />
              {/* <h2
              className="text-4xl md:text-5xl mb-6 leading-tight"
              style={{
                fontFamily: "Cormorant Garamond, serif",
                fontWeight: 600,
              }}
            >
              Book Your Stay Today & Experience Luxury Like Never Before
            </h2>
            <p
              className="text-xl text-white/80 mb-10"
              style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
            >
              Limited availability for premium rooms and villas
            </p> */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href={`tel:${PhoneNo}`}
                  whileHover={{ scale: 1.05 }}
                  className="bg-[#d4af37] text-[#2a2520] px-10 py-4 shadow-xl hover:bg-[#c9a961] transition-colors text-lg inline-block"
                  style={{ fontFamily: "Jost, sans-serif", fontWeight: 600 }}
                >
                  Call to Book Now
                </motion.a>
                <motion.div whileHover={{ scale: 1.05 }}>
                  <Link
                    href="/rooms/"
                    className="border-2 border-white text-white px-10 py-4 hover:bg-white hover:text-[#2a2520] transition-all text-lg inline-block"
                    style={{ fontFamily: "Jost, sans-serif", fontWeight: 600 }}
                  >
                    Explore Rooms
                  </Link>
                </motion.div>
              </div>
            </FadeInSection>
          </div>
        </section>
      )}
    </div>
  );
}
